import { useState } from 'react'
import { useLocation } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { api } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { practitionerTypeGroups, SUGGESTED_GROUP_LABEL } from '@/lib/practitioner-types'
import { needsProfession, isNudgeSnoozed, snoozeNudge } from '@/lib/role-nudge'

/**
 * "Set your role" — the profession nudge banner (CLA-1141 follow-up).
 *
 * Answers IN PLACE rather than linking to Settings. The clinic-setup banner
 * next door sends people to a page because a clinic profile is several fields;
 * this is one field, and handing someone a settings page with fifteen others on
 * it is where a two-second answer goes to die.
 *
 * Hidden on /settings, where the field itself carries the same flag — a banner
 * nagging about a control already visible on screen reads as a bug.
 *
 * Copy is deliberately in the product's voice, not a first person one: Claro
 * says what it does, it does not talk about itself as "I".
 *
 * Gate, snooze and the admin_only exclusion all live in `@/lib/role-nudge`;
 * see that file for why each rule is there.
 */
export function PractitionerRoleBanner() {
  const user = useAuthStore((s) => s.user)
  const { pathname } = useLocation()
  const qc = useQueryClient()

  // null = untouched, so the AHPRA suggestion can stand as the default without
  // overwriting a deliberate choice — including a deliberate change back to the
  // placeholder, which `'' || suggestion` would silently undo.
  const [choice, setChoice] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const applies = needsProfession(user) && !!user && !isNudgeSnoozed(user.id)
  // CLA-1147: when the user's own AHPRA number says which board registered
  // them, the shortlist goes to the top and the ask gets smaller.
  const groups = practitionerTypeGroups(user?.practitioner_type_candidates)
  const narrowed = groups[0]?.label === SUGGESTED_GROUP_LABEL
  // Pre-fill, don't make people type: the shortlist is ordered most-likely
  // first, so its head is the presumptive answer — Registered Nurse for an NMW
  // registration, and the only candidate at all for MED, PHA, DEN, PSY, PHY and
  // PAR. It is a DEFAULT, never a write: nothing is stored until the clinician
  // presses Confirm, and the copy names the guess rather than quietly selecting
  // it, so confirming is a decision and not a reflex.
  const suggestion = narrowed ? groups[0].options[0] : null
  const value = choice ?? suggestion?.value ?? ''
  const onSettings = pathname.startsWith('/settings')
  if (!applies || dismissed || onSettings) return null

  async function save() {
    if (!value || !user) return
    setSaving(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('practitioner_type', value)
      await api.postForm('/settings', form)
      qc.invalidateQueries({ queryKey: ['settings'] })
      // Refresh the auth store rather than hiding locally: `user.practitioner_type`
      // is what closes this gate AND clears the Settings flag and sidebar dot, so
      // all three surfaces resolve from one source instead of drifting apart.
      await useAuthStore.getState().init()
      // Verify by reading back, not by assuming the 200. A write-only save
      // that silently didn't stick would otherwise leave this banner stuck on
      // "Saving…" forever, which reads as a hang rather than a failure.
      if (!useAuthStore.getState().user?.practitioner_type) {
        setError("That didn't save — try again.")
        setSaving(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't save — try again.")
      setSaving(false)
    }
  }

  function notNow() {
    if (user) snoozeNudge(user.id)
    setDismissed(true)
  }

  return (
    <div className="bg-warning-light border-b border-warning/25 px-4 py-2 flex items-center gap-3 flex-wrap shrink-0">
      <AlertCircle className="w-4 h-4 text-warning shrink-0" aria-hidden />
      <p className="flex-1 min-w-[240px] text-[13px] text-on-warning-soft">
        <span className="font-semibold">
          {suggestion
            ? `Your AHPRA registration suggests you're a ${suggestion.label}.`
            : 'Notes read differently for a doctor, a nurse and a dermal therapist.'}
        </span>{' '}
        {error ??
          (suggestion
            ? 'Confirm it, or pick the right one — notes are written to match.'
            : 'Set your role so yours are written to match.')}
      </p>
      <select
        value={value}
        onChange={(e) => setChoice(e.target.value)}
        disabled={saving}
        aria-label="Your role"
        className="shrink-0 min-w-[190px] text-sm text-ink bg-surface border border-warning/40 rounded-lg px-2.5 py-1.5 outline-none cursor-pointer disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-claro-600"
      >
        <option value="">Select your role</option>
        {groups.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <button
        onClick={() => void save()}
        disabled={!value || saving}
        className="shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-claro-600 rounded-lg hover:bg-claro-700 transition-colors disabled:bg-primary-disabled disabled:text-on-primary-disabled disabled:cursor-not-allowed"
      >
        {saving ? 'Saving…' : suggestion ? 'Confirm' : 'Save'}
      </button>
      <button
        onClick={notNow}
        disabled={saving}
        className="shrink-0 px-2 py-1.5 text-xs font-medium text-on-warning-soft hover:underline transition-colors"
      >
        Not now
      </button>
    </div>
  )
}
